import React, { ReactNode, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  nextStep,
  finalStep,
  goTo,
} from '../../../../../redux/actions/merchants/products/purchases';
import PurchaseStep, {
  StepItems,
} from '../../../../../molecules/merchant/products/purchases/PurchaseStep';

import Completion from '../../../../../molecules/merchant/products/purchases/Completion';
import Confirmation from '../Confirmation';
import Customer from '../Customer';
import CompletionFailed from '../../../../../molecules/merchant/products/purchases/CompletionFailed';
import Payment from '../Payment';
import { ReduxState } from '../../../../../redux/reducers';
import { getPurchases } from '../../../../../redux/selectors/merchants/products/purchases';
import ProductPurchaseAuth from '../ProductPurchaseAuth';
import { useAppDispatch } from '../../../../../redux';

type SettingParams = {
  displayProgress: boolean;
  currentProgress: StepItems | null;
  renderFn: () => ReactNode;
};

const FULL_WORKFLOW_STEPS = [
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_CUSTOMER_WORKFLOW_STEPS = [
  StepItems.Payment,
  StepItems.Confirmation,
];

const RETURN_TARGET_STEP = 97;
const RESTART_STEP = 98;

type Props = {
  isEmailVerified: boolean;
  isLoggedIn: boolean;
};

const SchemeTypeTicketBook: React.FC<Props> = ({
  isEmailVerified,
  isLoggedIn,
}) => {
  const dispatch = useAppDispatch();

  const { merchantProduct, publicId, postable, settings, step } = useSelector(
    (state: ReduxState) => getPurchases(state.merchantsProductsPurchases),
    shallowEqual,
  );

  const renderAuthAt = useCallback(
    (step: number) => {
      if (settings === null) return null;
      return (
        <ProductPurchaseAuth
          schemeType={settings.schemeType}
          postable={postable}
          moveToNext={() => dispatch(nextStep(step))}
          isEmailVerified={isEmailVerified}
          isLoggedIn={isLoggedIn}
        />
      );
    },
    [settings, postable, dispatch, isEmailVerified, isLoggedIn],
  );

  const renderCustomerAt = useCallback(
    (step: number) => {
      if (settings === null) return null;
      return (
        <Customer
          schemeType={settings.schemeType}
          settings={settings.customer}
          postable={postable}
          onSubmit={() => dispatch(nextStep(step))}
        />
      );
    },
    [settings, postable, dispatch],
  );

  const renderPaymentAt = useCallback(
    (step: number) => {
      if (!settings || !settings.payment) return null;
      return (
        <Payment
          schemeType={settings.schemeType}
          settings={settings.payment}
          merchantPublicId={publicId}
          postable={postable}
          onSubmit={() => dispatch(nextStep(step))}
        />
      );
    },
    [settings, postable, dispatch, publicId],
  );

  const renderConfirmationAt = useCallback(
    (step: number) => {
      if (!settings || !merchantProduct) return null;
      return (
        <Confirmation
          schemeType={settings.schemeType}
          product={merchantProduct}
          firstCharge={null}
          settings={settings.confirmation}
          postable={postable}
          publicId={publicId}
          onSubmit={() => dispatch(finalStep(step))}
          onRestart={() => dispatch(goTo(RESTART_STEP))}
          onReturnTargetStep={() => dispatch(goTo(RETURN_TARGET_STEP))}
        />
      );
    },
    [settings, merchantProduct, postable, publicId, dispatch],
  );

  const renderCompletion = useCallback(() => {
    if (settings === null || !merchantProduct) return null;
    return (
      <Completion
        schemeType={settings.schemeType}
        settings={settings.completion}
        product={merchantProduct}
        firstCharge={null}
        lastOrder={null}
        publicId={publicId}
      />
    );
  }, [settings, merchantProduct, publicId]);

  const renderFailedRestart = useCallback(() => {
    const backTo = {
      label: 'purchases.schemeTypeTicketBook.restart',
      modifyPage: () => location.reload(),
    };
    if (settings === null) return null;
    return (
      <CompletionFailed
        schemeType={settings.schemeType}
        settings={settings.completion!}
        backTo={backTo}
      />
    );
  }, [settings]);

  const renderFailedReturnTargetStep = useCallback(
    (modifierStep: number) => {
      const backTo = {
        label: 'purchases.schemeTypeTicketBook.returnTargetStep',
        modifyPage: () => dispatch(goTo(modifierStep)),
      };
      if (settings === null) return null;
      return (
        <CompletionFailed
          schemeType={settings.schemeType}
          settings={settings.completion!}
          backTo={backTo}
        />
      );
    },
    [settings, dispatch],
  );

  const fullWorkFlowSetting = useCallback((): [
    Array<StepItems>,
    SettingParams | undefined,
  ] => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderCustomerAt(1)        },
      2: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderPaymentAt(2)         },
      3: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderConfirmationAt(3)    },
      4: { displayProgress: false, currentProgress: null,                   renderFn: () => renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedRestart()      },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderAuthAt,
    renderCustomerAt,
    renderPaymentAt,
    renderConfirmationAt,
    renderCompletion,
    renderFailedRestart,
    renderFailedReturnTargetStep,
  ]);

  const skipCustomerFlowSetting = useCallback((): [
    Array<StepItems>,
    SettingParams | undefined,
  ] => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderPaymentAt(1)         },
      2: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderConfirmationAt(2)    },
      3: { displayProgress: false, currentProgress: null,                   renderFn: () => renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedRestart()      },
    }[step];

    return [SKIP_CUSTOMER_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderAuthAt,
    renderPaymentAt,
    renderConfirmationAt,
    renderCompletion,
    renderFailedRestart,
    renderFailedReturnTargetStep,
  ]);

  const [allSteps, setting] = useMemo((): [
    Array<StepItems>,
    SettingParams | undefined,
  ] => {
    return settings !== null && settings.customer.customerFields!.length !== 0
      ? fullWorkFlowSetting()
      : skipCustomerFlowSetting();
  }, [settings, fullWorkFlowSetting, skipCustomerFlowSetting]);

  return (
    <>
      {setting && setting.displayProgress && setting.currentProgress && (
        <PurchaseStep
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default SchemeTypeTicketBook;
